import { useParams, Link, useLocation } from "wouter";
import { useGetJob, getGetJobQueryKey, useRetryJob, useDeleteJob, useRegenerateJob, getListJobsQueryKey, getGetJobStatsQueryKey, useGetJobTimingStats, getGetJobTimingStatsQueryKey, useGetCurrentUser, useGetSocialPostByJob, getGetSocialPostByJobQueryKey, useGetJobQueuePosition, getGetJobQueuePositionQueryKey } from "@workspace/api-client-react";
import { useState, useRef, useEffect, useCallback } from "react";
import { useAudioUrlRefresh } from "@/hooks/useAudioUrlRefresh";
import { reportPlaybackError } from "@/lib/playbackTelemetry";
import { useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@clerk/react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Play, Pause, Download, RotateCcw, Activity, Clock,
  CheckCircle2, AlertTriangle, FileTerminal, Scissors, HardDrive,
  Ban, ArrowRight, TimerOff, Info, Timer, RefreshCw, Archive, Trash2,
  AlertCircle, Loader2, Image, Film, Cpu,
} from "lucide-react";
import { CreateVideoSection } from "@/components/CreateVideoSection";

const basePath = import.meta.env.BASE_URL.replace(/\/$/, "");

const SOCIAL_POST_TERMINAL_STATUSES = new Set(["approved", "discarded", "failed", "partial", "published"]);

export default function StatusPage() {
  const params = useParams();
  const jobId = parseInt(params.id || "0", 10);
  const queryClient = useQueryClient();
  const { getToken } = useAuth();
  const [, setLocation] = useLocation();

  const { data: job, isLoading, isError } = useGetJob(jobId, {
    query: {
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return (status === "ready" || status === "failed" || status === "blocked") ? false : 2000;
      },
      queryKey: getGetJobQueryKey(jobId)
    }
  });

  const [pendingMode, setPendingMode] = useState<'retry' | 'retranscribe' | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();

  const audioRef = useRef<HTMLAudioElement>(null);
  const { audioUrl, setAudioUrl, audioExpired, setAudioExpired, pendingRestoreRef, lastRefreshAtRef, expiresAtRef } = useAudioUrlRefresh({
    jobId,
    jobStatus: job?.status,
    jobOutputUrl: job?.outputUrl,
    getToken,
    audioRef,
  });

  const { mutate: regenerateForDownload, isPending: isRegeneratingForDownload } = useRegenerateJob({
    mutation: {
      onSuccess: () => {
        setAudioExpired(false);
        queryClient.invalidateQueries({ queryKey: getGetJobQueryKey(jobId) });
      },
      onError: (err: unknown) => {
        const msg = (err as { message?: string })?.message ?? "Could not regenerate this job. Please try again.";
        toast({ title: "Regenerate failed", description: msg, variant: "destructive" });
      },
    },
  });

  const { data: currentUser } = useGetCurrentUser();

  const { data: socialPostData } = useGetSocialPostByJob(jobId, {
    query: {
      retry: false,
      enabled: !!jobId,
      queryKey: getGetSocialPostByJobQueryKey(jobId),
      refetchInterval: (query) => {
        const post = query.state.data?.post;
        if (!post) return 5000;
        if (SOCIAL_POST_TERMINAL_STATUSES.has(post.status ?? "")) return false;
        if (post.thumbnailUrl && post.videoUrl) return false;
        return 5000;
      },
    },
  });
  const socialPost = socialPostData?.post ?? null;

  const [thumbnailBlobUrl, setThumbnailBlobUrl] = useState<string | null>(null);
  const [isDownloadingThumbnail, setIsDownloadingThumbnail] = useState(false);
  const [isDownloadingVideo, setIsDownloadingVideo] = useState(false);

  useEffect(() => {
    if (!socialPost?.thumbnailUrl || !socialPost?.id) return;
    let revoked = false;
    (async () => {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/social/posts/${socialPost.id}/thumbnail`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok || revoked) return;
      const blob = await res.blob();
      if (revoked) return;
      const url = URL.createObjectURL(blob);
      setThumbnailBlobUrl(url);
    })();
    return () => {
      revoked = true;
      setThumbnailBlobUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return null; });
    };
  }, [socialPost?.id, socialPost?.thumbnailUrl, getToken]);

  const handleDownloadThumbnail = useCallback(async () => {
    if (isDownloadingThumbnail || !socialPost?.id || !socialPost?.thumbnailUrl) return;
    setIsDownloadingThumbnail(true);
    try {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/social/posts/${socialPost.id}/thumbnail`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `social_thumbnail_job_${jobId}.jpg`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch {
      toast({ title: "Download failed", description: "Could not download the thumbnail.", variant: "destructive" });
    } finally {
      setIsDownloadingThumbnail(false);
    }
  }, [isDownloadingThumbnail, socialPost?.id, socialPost?.thumbnailUrl, getToken, jobId, toast]);

  const handleDownloadVideo = useCallback(async () => {
    if (isDownloadingVideo || !socialPost?.id || !socialPost?.videoUrl) return;
    setIsDownloadingVideo(true);
    try {
      const token = await getToken();
      const res = await fetch(`${basePath}/api/social/posts/${socialPost.id}/video-url`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("Download failed");
      const { url } = await res.json() as { url: string };
      const a = document.createElement("a");
      a.href = url;
      a.download = `social_video_job_${jobId}.mp4`;
      a.click();
    } catch {
      toast({ title: "Download failed", description: "Could not download the video.", variant: "destructive" });
    } finally {
      setIsDownloadingVideo(false);
    }
  }, [isDownloadingVideo, socialPost?.id, socialPost?.videoUrl, getToken, jobId, toast]);

  const { mutate: rerunAbbreviation, isPending: isRerunning } = useRegenerateJob({
    mutation: {
      onSuccess: (data) => {
        setLocation(`/jobs/${data.id}`);
      },
      onError: (err: unknown) => {
        const msg = (err as { message?: string })?.message ?? "Could not re-run this job. Please try again.";
        toast({ title: "Re-run failed", description: msg, variant: "destructive" });
      },
    },
  });

  const { mutate: deleteJob, isPending: isDeleting } = useDeleteJob({
    mutation: {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListJobsQueryKey() });
        queryClient.invalidateQueries({ queryKey: getGetJobStatsQueryKey() });
        setLocation("/jobs");
      },
      onError: () => {
        setShowDeleteConfirm(false);
        toast({ title: "Delete failed", description: "Could not delete this job. Please try again.", variant: "destructive" });
      },
    },
  });

  const retryMutation = useRetryJob({
    mutation: {
      onSuccess: () => {
        setPendingMode(null);
        queryClient.invalidateQueries({ queryKey: getGetJobQueryKey(jobId) });
      },
      onError: () => {
        setPendingMode(null);
      },
    },
  });

  const isExpired = job?.expiresAt && new Date(job.expiresAt) < new Date();

  // ── Timing stats (for historically-grounded ETA) ─────────────────────────
  const isProcessing = isActiveStepProcessing(job?.status ?? "");
  const { data: timingStats } = useGetJobTimingStats({
    query: { enabled: isProcessing, staleTime: 5 * 60 * 1000, queryKey: getGetJobTimingStatsQueryKey() }
  });

  // ── Live queue position (only meaningful while the job is queued) ────────
  // Polled every 5s so users see "X jobs ahead of you" updating during a
  // large RSS backfill. Polling stops automatically once the job leaves the
  // "queued" state.
  const isQueued = job?.status === "queued";
  const { data: queuePosition } = useGetJobQueuePosition(jobId, {
    query: {
      enabled: !!jobId && isQueued,
      queryKey: getGetJobQueuePositionQueryKey(jobId),
      refetchInterval: (query) => {
        const status = query.state.data?.status;
        return status === "queued" ? 5000 : false;
      },
    },
  });

  // ── Elapsed time tracking ─────────────────────────────────────────────────
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  useEffect(() => {
    if (!isProcessing || !job?.processingStartedAt) {
      setElapsedSeconds(0);
      return;
    }
    const startedAt = new Date(job.processingStartedAt).getTime();
    const tick = () => setElapsedSeconds(Math.max(0, Math.floor((Date.now() - startedAt) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [job?.status, job?.processingStartedAt]);

  const [isPlaying, setIsPlaying] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDownloading, setIsDownloading] = useState(false);

  const handleDownload = async () => {
    if (isDownloading || !job?.outputUrl) return;
    setIsDownloading(true);
    try {
      const token = await getToken();
      const headers: Record<string, string> = token ? { Authorization: `Bearer ${token}` } : {};
      const urlRes = await fetch(`${basePath}/api/jobs/${jobId}/audio-url`, { headers });
      if (urlRes.status === 410) {
        setAudioExpired(true);
        return;
      }
      let fetchUrl: string;
      let fetchInit: RequestInit = {};
      if (urlRes.ok) {
        const { url: signedUrl } = await urlRes.json() as { url: string; expiresAt: string };
        fetchUrl = signedUrl;
      } else if (urlRes.status === 404) {
        fetchUrl = `${basePath}/api/jobs/${jobId}/download`;
        fetchInit = { headers };
      } else {
        throw new Error("Download failed");
      }
      const audioRes = await fetch(fetchUrl, fetchInit);
      if (!audioRes.ok) throw new Error("Download failed");
      const blob = await audioRes.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `abbreviated_job_${jobId}.mp3`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } finally {
      setIsDownloading(false);
    }
  };

  useEffect(() => {
    if (audioUrl && audioRef.current) {
      const audio = audioRef.current;
      const setAudioData = () => {
        setDuration(audio.duration);
        const restore = pendingRestoreRef.current;
        if (restore) {
          pendingRestoreRef.current = null;
          audio.currentTime = restore.time;
          if (restore.wasPlaying) {
            audio.play().catch(() => {});
          }
        }
      };
      const setAudioTime = () => setCurrentTime(audio.currentTime);
      const handleEnded = () => { setIsPlaying(false); setIsBuffering(false); };
      const handlePause = () => { setIsPlaying(false); setIsBuffering(false); };
      const handlePlaying = () => setIsBuffering(false);
      const handleWaiting = () => setIsBuffering(true);
      const handleError = () => {
        setIsPlaying(false);
        setIsBuffering(false);
        void reportPlaybackError({
          audio,
          jobId,
          page: "status",
          eventType: "error",
          lastRefreshAt: lastRefreshAtRef.current,
          expiresAt: expiresAtRef.current,
          getToken,
        });
        const code = audio.error?.code;
        const description =
          code === 2
            ? "Your network blocked the audio stream. Try refreshing the page."
            : code === 3
            ? "The audio file couldn't be decoded. Try downloading it instead."
            : code === 4
            ? "The audio stream couldn't be loaded. Try refreshing the page."
            : "Something went wrong during playback. Please try again.";
        toast({ title: "Playback error", description, variant: "destructive" });
      };

      let recoveryTimer: ReturnType<typeof setTimeout> | null = null;
      const attemptRecovery = () => {
        if (recoveryTimer) return;
        void reportPlaybackError({
          audio,
          jobId,
          page: "status",
          eventType: "stalled",
          lastRefreshAt: lastRefreshAtRef.current,
          expiresAt: expiresAtRef.current,
          getToken,
        });
        recoveryTimer = setTimeout(() => {
          recoveryTimer = null;
          if (!audio.paused && !audio.ended) {
            const pos = audio.currentTime;
            audio.currentTime = pos + 0.001;
            audio.play().catch(() => {
              setIsPlaying(false);
              setIsBuffering(false);
            });
          }
        }, 300);
      };

      audio.addEventListener("loadedmetadata", setAudioData);
      audio.addEventListener("timeupdate", setAudioTime);
      audio.addEventListener("ended", handleEnded);
      audio.addEventListener("pause", handlePause);
      audio.addEventListener("playing", handlePlaying);
      audio.addEventListener("stalled", attemptRecovery);
      audio.addEventListener("waiting", handleWaiting);
      audio.addEventListener("waiting", attemptRecovery);
      audio.addEventListener("error", handleError);

      return () => {
        if (recoveryTimer) clearTimeout(recoveryTimer);
        audio.removeEventListener("loadedmetadata", setAudioData);
        audio.removeEventListener("timeupdate", setAudioTime);
        audio.removeEventListener("ended", handleEnded);
        audio.removeEventListener("pause", handlePause);
        audio.removeEventListener("playing", handlePlaying);
        audio.removeEventListener("stalled", attemptRecovery);
        audio.removeEventListener("waiting", handleWaiting);
        audio.removeEventListener("waiting", attemptRecovery);
        audio.removeEventListener("error", handleError);
      };
    }
    return undefined;
  }, [audioUrl]);

  const togglePlayPause = () => {
    if (audioRef.current) {
      if (audioRef.current.paused) {
        audioRef.current.play().then(() => {
          setIsPlaying(true);
        }).catch(() => {
          setIsPlaying(false);
        });
      } else {
        audioRef.current.pause();
        setIsPlaying(false);
      }
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = Number(e.target.value);
    if (audioRef.current) {
      audioRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "00:00";
    const m = Math.floor(time / 60);
    const s = Math.floor(time % 60);
    return `${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="animate-spin text-primary">
          <Activity size={32} />
        </div>
      </div>
    );
  }

  if (isError || !job) {
    return (
      <div className="max-w-xl mx-auto mt-12 text-center space-y-4">
        <AlertTriangle className="mx-auto h-12 w-12 text-destructive" />
        <h2 className="text-xl font-bold">Job Not Found</h2>
        <p className="text-muted-foreground">The requested job could not be found or there was a server error.</p>
        <Button asChild variant="outline">
          <Link href="/">New Job</Link>
        </Button>
      </div>
    );
  }

  const pipelineSteps = ["uploading", "analyzing", "abbreviating", "ready"];
  const statusToIndex: Record<string, number> = {
    queued: 1,
    analyzing: 1,
    abbreviating: 2,
    ready: 3,
    failed: -1,
  };
  let currentStepIndex = job.status in statusToIndex ? statusToIndex[job.status] : 0;
  if (job.status === "failed") currentStepIndex = -1;

  return (
    <div className="max-w-3xl mx-auto space-y-8 animate-in fade-in duration-500">
      
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-mono font-bold tracking-tight truncate max-w-lg" title={job.originalFilename}>{job.originalFilename}</h1>
          <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground font-mono">
            <span className="text-primary">{job.targetPercent}% target</span>
            {job.expiresAt && (
              <>
                <span>•</span>
                <span className={`flex items-center gap-1 ${isExpired ? 'text-destructive' : ''}`}>
                  <TimerOff size={14} /> {isExpired ? 'Expired' : `Expires ${new Date(job.expiresAt).toLocaleDateString()}`}
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex gap-2 items-start flex-wrap justify-end">
          {job.status === "ready" && !isExpired && (
            <>
              <Button asChild variant="default" className="font-mono uppercase text-xs tracking-wider" data-testid="button-view-results">
                <Link href={`/jobs/${job.id}/result`}>
                  <CheckCircle2 className="mr-2 h-4 w-4" /> View Results
                </Link>
              </Button>
              {job.outputUrl && (
                <Button
                  variant="outline"
                  className="font-mono uppercase text-xs tracking-wider"
                  data-testid="button-download"
                  onClick={handleDownload}
                  disabled={isDownloading}
                >
                  <Download className="mr-2 h-4 w-4" />
                  {isDownloading ? "Downloading…" : "Download"}
                </Button>
              )}
            </>
          )}
          {job.status === "ready" && (job.sourceType === "rss" || (job.sourceType === "file" && job.sourceExpiresAt && new Date(job.sourceExpiresAt) > new Date())) && (
            <Button
              variant="outline"
              className="font-mono uppercase text-xs tracking-wider"
              onClick={() => rerunAbbreviation({ id: jobId, data: { mode: (job.mode ?? "full_recap") as "key_quotes" | "quick_catchup" | "full_recap", targetPercent: job.targetPercent ?? 40 } })}
              disabled={isRerunning}
              data-testid="button-rerun"
            >
              {isRerunning ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              {isRerunning ? "Starting…" : "Re-run Abbreviation"}
            </Button>
          )}
          {isActiveStepProcessing(job.status) ? (
            <div className="flex flex-col items-end gap-1">
              <Button
                variant="outline"
                className="font-mono uppercase text-xs tracking-wider text-muted-foreground"
                title="Cannot delete a job that is currently processing"
                disabled
                data-testid="button-delete-disabled"
              >
                <Trash2 className="mr-2 h-4 w-4" /> Delete
              </Button>
              <span className="text-xs text-muted-foreground font-mono">Processing in progress</span>
            </div>
          ) : showDeleteConfirm ? (
            <div className="flex gap-2 items-center">
              <span className="text-xs font-mono text-muted-foreground">Delete this job?</span>
              <Button
                variant="destructive"
                className="font-mono uppercase text-xs tracking-wider"
                onClick={() => deleteJob({ id: jobId })}
                disabled={isDeleting}
                data-testid="button-delete-confirm"
              >
                {isDeleting ? "Deleting…" : "Confirm"}
              </Button>
              <Button
                variant="outline"
                className="font-mono uppercase text-xs tracking-wider"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
                data-testid="button-delete-cancel"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              variant="outline"
              className="font-mono uppercase text-xs tracking-wider text-destructive hover:text-destructive hover:bg-destructive/10 border-destructive/30"
              onClick={() => setShowDeleteConfirm(true)}
              data-testid="button-delete"
            >
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </Button>
          )}
        </div>
      </div>

      {job.status === "ready" && job.sourceType === "rss" && currentUser && (
        <p className="text-xs text-muted-foreground font-mono" data-testid="rerun-helper-text">
          {currentUser.plan === "pro"
            ? `Re-running will use ~${Math.round((job.originalDurationSeconds ?? 0) / 60)} min from your 50 hr/month pool.`
            : `${Math.max(0, 5 - currentUser.usageCount)} of 5 monthly abbreviations remaining.`}
        </p>
      )}
      {job.status === "ready" && job.sourceType === "file" && job.sourceExpiresAt && new Date(job.sourceExpiresAt) > new Date() && currentUser && (
        <p className="text-xs text-muted-foreground font-mono" data-testid="rerun-helper-text-file">
          {`Source file available for re-run until ${new Date(job.sourceExpiresAt).toLocaleString()}. `}
          {currentUser.plan === "pro"
            ? `Re-running will use ~${Math.round((job.originalDurationSeconds ?? 0) / 60)} min from your 50 hr/month pool.`
            : `${Math.max(0, 5 - currentUser.usageCount)} of 5 monthly abbreviations remaining.`}
        </p>
      )}

      {job.warnings && job.warnings.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-yellow-500/30 bg-yellow-500/10 px-4 py-3" data-testid="panel-job-warnings">
          <AlertTriangle size={16} className="text-yellow-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0 space-y-1">
            {job.warnings.map((warning, idx) => (
              <p key={idx} className="text-sm font-mono text-yellow-200 leading-snug">{warning}</p>
            ))}
          </div>
        </div>
      )}

      {audioExpired && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3" data-testid="panel-audio-expired">
          <AlertCircle size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-mono text-amber-200 leading-snug">
              Audio has expired — regenerate this job to download it.
            </p>
            <Button
              size="sm"
              variant="outline"
              onClick={() => regenerateForDownload({ id: jobId, data: { mode: (job?.mode ?? "full_recap") as "key_quotes" | "quick_catchup" | "full_recap", targetPercent: job?.targetPercent ?? 40 } })}
              disabled={isRegeneratingForDownload}
              className="mt-2 font-mono text-xs gap-1.5 border-amber-500/40 text-amber-200 hover:bg-amber-500/10"
              data-testid="button-regenerate-expired"
            >
              {isRegeneratingForDownload ? (
                <Loader2 size={12} className="animate-spin" />
              ) : (
                <RefreshCw size={12} />
              )}
              {isRegeneratingForDownload ? "Regenerating…" : "Regenerate Job"}
            </Button>
          </div>
        </div>
      )}

      <Card className="bg-card/50 border-border/50">
        <CardHeader className="pb-4 border-b border-border/10">
          <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
            <Activity size={16} className="text-primary" /> 
            Pipeline Status
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6">
          
          {job.status === "blocked" ? (
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 bg-amber-500/10 text-amber-500 rounded-full flex items-center justify-center mx-auto">
                <Ban size={32} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-amber-500 uppercase tracking-wider font-mono">Episode Blocked</h3>
                <p className="text-muted-foreground mt-4 text-sm max-w-sm mx-auto">
                  You've reached the free plan limit (5 episodes/month). Upgrade to Pro to process this episode.
                </p>
              </div>
              <div className="pt-2 flex gap-3 justify-center">
                <Button className="bg-indigo-500 hover:bg-indigo-600" onClick={() => setLocation("/billing")}>
                  Upgrade to Pro — $19/month <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <Button asChild variant="outline">
                  <Link href="/jobs">View History</Link>
                </Button>
              </div>
            </div>
          ) : job.status === "failed" ? (
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 bg-destructive/10 text-destructive rounded-full flex items-center justify-center mx-auto">
                <AlertTriangle size={32} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-destructive uppercase tracking-wider font-mono">Pipeline Failure</h3>
                <p className="text-muted-foreground mt-1 bg-destructive/5 p-4 rounded-md font-mono text-sm border border-destructive/20 inline-block text-left mt-4">
                  {job.errorMessage || "Unknown processing error occurred"}
                </p>
              </div>
              {retryMutation.isError && (
                <p className="text-sm text-destructive font-mono">
                  {(retryMutation.error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? "Retry failed. Please re-upload your file."}
                </p>
              )}
              <div className="pt-4 flex flex-col items-center gap-3">
                {job.status === "failed" && (
                  <div className="flex flex-wrap gap-3 justify-center">
                    <Button
                      variant="default"
                      disabled={retryMutation.isPending}
                      onClick={() => { setPendingMode('retry'); retryMutation.mutate({ id: jobId, data: {} }); }}
                      className="font-mono uppercase text-xs tracking-wider"
                    >
                      <RotateCcw className={`mr-2 h-4 w-4 ${pendingMode === 'retry' ? "animate-spin" : ""}`} />
                      {pendingMode === 'retry' ? "Retrying..." : "Retry Job"}
                    </Button>
                    <Button
                      variant="outline"
                      disabled={retryMutation.isPending}
                      onClick={() => { setPendingMode('retranscribe'); retryMutation.mutate({ id: jobId, data: { forceRetranscribe: true } }); }}
                      className="font-mono uppercase text-xs tracking-wider"
                      title="Re-runs transcription from scratch — use this if the transcript had poor quality or wrong speaker labels"
                    >
                      <RotateCcw className={`mr-2 h-4 w-4 ${pendingMode === 'retranscribe' ? "animate-spin" : ""}`} />
                      {pendingMode === 'retranscribe' ? "Retrying..." : "Retry with Fresh Transcript"}
                    </Button>
                  </div>
                )}
                <Button asChild variant="outline" className="font-mono uppercase text-xs tracking-wider">
                  <Link href="/">New Job</Link>
                </Button>
              </div>
            </div>
          ) : isExpired ? (
            <div className="text-center py-8 space-y-4">
              <div className="h-16 w-16 bg-muted text-muted-foreground rounded-full flex items-center justify-center mx-auto">
                <TimerOff size={32} />
              </div>
              <div>
                <h3 className="text-lg font-bold text-muted-foreground uppercase tracking-wider font-mono text-sm">Job Expired</h3>
                <p className="text-muted-foreground mt-4 text-sm max-w-sm mx-auto">
                  Processing results are kept for 30 days (90 for Pro). This job has been deleted from our servers.
                </p>
              </div>
              <div className="pt-2 flex gap-3 justify-center">
                <Button asChild variant="outline">
                  <Link href="/">New Job</Link>
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-8">
              <div className="relative">
                <div className="absolute top-1/2 left-0 w-full h-0.5 bg-secondary -translate-y-1/2 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary transition-all duration-500 ease-in-out" 
                    style={{ width: `${Math.max(0, currentStepIndex) * (100 / (pipelineSteps.length - 1))}%` }}
                  />
                </div>
                <div className="relative flex justify-between">
                  {pipelineSteps.map((step, index) => {
                    const isCompleted = index < currentStepIndex;
                    const isActive = index === currentStepIndex;
                    return (
                      <div key={step} className="flex flex-col items-center gap-2">
                        <div className={`
                          h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold z-10 transition-colors
                          ${isCompleted ? 'bg-primary text-primary-foreground' : 
                            isActive ? 'bg-card border-2 border-primary text-primary shadow-[0_0_15px_rgba(6,182,212,0.5)]' : 
                            'bg-secondary text-muted-foreground border-2 border-transparent'}
                        `}>
                          {isCompleted ? <CheckCircle2 size={14} /> : (index + 1)}
                        </div>
                        <span className={`text-xs font-mono uppercase tracking-wider ${isActive ? 'text-primary font-bold' : 'text-muted-foreground'}`}>
                          {step}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              
              {isActiveStepProcessing(job.status) && (
                <ProcessingTimeCard
                  status={job.status}
                  elapsedSeconds={elapsedSeconds}
                  originalDurationSeconds={job.originalDurationSeconds ?? null}
                  timingStats={timingStats ?? null}
                  transcriptProgress={job.transcriptProgress ?? null}
                  queuePosition={queuePosition ?? null}
                />
              )}
              {job.transcriptReused !== null && job.transcriptReused !== undefined && (
                <TranscriptSourceBadge reused={job.transcriptReused} />
              )}
            </div>
          )}

        </CardContent>
      </Card>

      {job.status === "ready" && !isExpired && (
        <>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="bg-card/50 border-border/50" data-testid="card-audio-player">
            <CardHeader className="pb-4 border-b border-border/10">
              <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                <Play size={16} className="text-primary" /> 
                Playback
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6">
              {/* See task #914 / result.tsx for the rationale on crossOrigin="anonymous". */}
              {audioUrl && <audio ref={audioRef} src={audioUrl} className="hidden" preload="metadata" crossOrigin="anonymous" />}
              
              <div className="space-y-6">
                <div className="flex items-center justify-center">
                  <button 
                    onClick={togglePlayPause}
                    data-testid="button-play-pause"
                    className="h-16 w-16 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground flex items-center justify-center transition-transform hover:scale-105 active:scale-95 shadow-lg shadow-primary/20"
                  >
                    {isBuffering
                      ? <Loader2 size={24} className="animate-spin" />
                      : isPlaying
                        ? <Pause size={24} fill="currentColor" />
                        : <Play size={24} fill="currentColor" className="ml-1" />}
                  </button>
                </div>
                
                <div className="space-y-2">
                  <input
                    type="range"
                    min={0}
                    max={duration || 100}
                    value={currentTime}
                    onChange={handleSeek}
                    data-testid="input-seek-bar"
                    className="w-full h-2 bg-secondary rounded-lg appearance-none cursor-pointer accent-primary"
                    style={{
                      background: `linear-gradient(to right, hsl(var(--primary)) ${(currentTime / (duration || 1)) * 100}%, hsl(var(--secondary)) ${(currentTime / (duration || 1)) * 100}%)`
                    }}
                  />
                  <div className="flex justify-between text-xs font-mono text-muted-foreground">
                    <span data-testid="text-current-time">{formatTime(currentTime)}</span>
                    <span data-testid="text-total-time">{formatTime(duration || job.outputDurationSeconds || 0)}</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card/50 border-border/50">
            <CardHeader className="pb-4 border-b border-border/10">
              <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
                <FileTerminal size={16} className="text-primary" /> 
                Result Metrics
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-6 space-y-4">
              <div className="flex justify-between items-center py-2 border-b border-border/20">
                <span className="text-sm font-mono text-muted-foreground flex items-center gap-2"><Clock size={14}/> Original Length</span>
                <span className="font-mono">{formatTime(job.originalDurationSeconds || 0)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/20">
                <span className="text-sm font-mono text-muted-foreground flex items-center gap-2"><Scissors size={14}/> Final Length</span>
                <span className="font-mono text-primary font-bold">{formatTime(job.outputDurationSeconds || 0)}</span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/20">
                <span className="text-sm font-mono text-muted-foreground flex items-center gap-2"><HardDrive size={14}/> Compression Achieved</span>
                <span className="font-mono">
                  {job.originalDurationSeconds && job.outputDurationSeconds 
                    ? Math.round((1 - (job.outputDurationSeconds / job.originalDurationSeconds)) * 100) 
                    : 0}%
                </span>
              </div>
              <div className="flex justify-between items-center py-2 border-b border-border/20">
                <span className="text-sm font-mono text-muted-foreground flex items-center gap-2"><Activity size={14}/> Segments Removed</span>
                <span className="font-mono">
                  {job.segmentsOriginal && job.segmentsFinal ? job.segmentsOriginal - job.segmentsFinal : 0}
                </span>
              </div>
              {job.transcriptReused !== null && job.transcriptReused !== undefined && (
                <div className="flex justify-between items-center py-2 border-b border-border/20">
                  <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
                    {job.transcriptReused ? <Archive size={14}/> : <RefreshCw size={14}/>} Transcript
                  </span>
                  <span className={`font-mono text-xs px-2 py-0.5 rounded-full border ${
                    job.transcriptReused
                      ? "bg-sky-500/10 text-sky-400 border-sky-500/20"
                      : "bg-violet-500/10 text-violet-400 border-violet-500/20"
                  }`}>
                    {job.transcriptReused ? "reused" : "regenerated"}
                  </span>
                </div>
              )}
              {job.transcriptModel !== null && job.transcriptModel !== undefined && (
                <div className="flex justify-between items-center py-2">
                  <span className="text-sm font-mono text-muted-foreground flex items-center gap-2">
                    <Cpu size={14}/> Model
                  </span>
                  <span className={`font-mono text-xs px-2 py-0.5 rounded-full border ${
                    job.transcriptModel === "nano"
                      ? "bg-amber-500/10 text-amber-400 border-amber-500/20"
                      : "bg-emerald-500/10 text-emerald-400 border-emerald-500/20"
                  }`}>
                    {job.transcriptModel}
                  </span>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
        {job.outputUrl && <CreateVideoSection job={job} jobId={jobId} />}
        </>
      )}

      {/* Social Post Preview — shown whenever a social post with assets is available,
          regardless of job status, so users see it even during in-progress pipeline stages */}
      {socialPost && (socialPost.thumbnailUrl || socialPost.videoUrl) && (
        <Card className="bg-card/50 border-border/50" data-testid="card-social-post">
          <CardHeader className="pb-4 border-b border-border/10">
            <CardTitle className="text-sm font-mono uppercase tracking-wider flex items-center gap-2">
              <Film size={16} className="text-primary" />
              Social Post
              {(() => {
                const s = socialPost.status;
                const label =
                  s === "pending_approval" ? "Pending Approval" :
                  s === "approved" ? "Approved" :
                  s === "discarded" ? "Discarded" :
                  s === "failed" ? "Failed" :
                  s === "partial" ? "Partially Published" :
                  s === "published" ? "Published" :
                  s === "generating" ? "Generating" : s;
                const variant =
                  s === "approved" || s === "published" ? "default" :
                  s === "discarded" || s === "failed" ? "destructive" :
                  "secondary";
                return (
                  <Badge variant={variant} className="font-mono text-xs ml-auto" data-testid="badge-social-post-status">
                    {label}
                  </Badge>
                );
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-5 space-y-5">
            {socialPost.thumbnailUrl && (
              <div className="space-y-2" data-testid="section-social-thumbnail">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Image size={12} /> Thumbnail
                </p>
                <div className="flex items-start gap-4">
                  {thumbnailBlobUrl ? (
                    <img
                      src={thumbnailBlobUrl}
                      alt="Social post thumbnail"
                      className="rounded-md border border-border/40 object-cover w-40 h-[90px] flex-shrink-0"
                      data-testid="img-social-thumbnail"
                    />
                  ) : (
                    <div className="w-40 h-[90px] rounded-md border border-border/40 bg-secondary/30 flex items-center justify-center flex-shrink-0">
                      <Loader2 size={16} className="animate-spin text-muted-foreground" />
                    </div>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    className="font-mono text-xs gap-1.5"
                    onClick={handleDownloadThumbnail}
                    disabled={isDownloadingThumbnail}
                    data-testid="button-download-thumbnail"
                  >
                    {isDownloadingThumbnail ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                    {isDownloadingThumbnail ? "Downloading…" : "Download Thumbnail"}
                  </Button>
                </div>
              </div>
            )}
            {socialPost.videoUrl && (
              <div className="space-y-2" data-testid="section-social-video">
                <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <Film size={12} /> Video
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="font-mono text-xs gap-1.5"
                  onClick={handleDownloadVideo}
                  disabled={isDownloadingVideo}
                  data-testid="button-download-video"
                >
                  {isDownloadingVideo ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                  {isDownloadingVideo ? "Downloading…" : "Download Video"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function TranscriptSourceBadge({ reused }: { reused: boolean }) {
  return (
    <div className={`flex items-center gap-2 text-xs font-mono px-3 py-1.5 rounded-md border w-fit ${
      reused
        ? "bg-sky-500/10 text-sky-400 border-sky-500/20"
        : "bg-violet-500/10 text-violet-400 border-violet-500/20"
    }`}>
      {reused ? <Archive size={12}/> : <RefreshCw size={12}/>}
      Transcript: {reused ? "reused from cache" : "regenerated"}
    </div>
  );
}

function isActiveStepProcessing(status: string) {
  return status === "analyzing" || status === "abbreviating";
}

/** Format elapsed seconds as "1:23" or "45s" */
function formatElapsed(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/** Format a seconds count as a friendly "~X min" / "~X sec" string */
function formatApprox(seconds: number): string {
  if (seconds < 60) return `~${Math.ceil(seconds)}s`;
  const m = Math.ceil(seconds / 30) * 0.5; // round up to nearest 30s → show in half-minutes
  if (m < 1) return `~${Math.ceil(seconds)}s`;
  if (m === 1) return "~1 min";
  if (Number.isInteger(m)) return `~${m} min`;
  return `~${m} min`;
}

interface JobTimingStatsData {
  avgAnalyzingSecondsPerAudioSecond: number | null;
  avgAbbreviatingSeconds: number | null;
  sampleCount: number;
}

// Heuristic constants (fallback when no historical data available)
const HEURISTIC_ANALYZING_RATIO = 1 / 10; // AssemblyAI ~10× realtime
const HEURISTIC_ANALYZING_OVERHEAD = 15;   // seconds
const HEURISTIC_ABBREVIATING_SECONDS = 45; // GPT-4o + FFmpeg flat estimate

/**
 * Compute an estimated total processing time in seconds.
 * Uses historical averages when available, falls back to heuristics.
 * History is considered available only when sampleCount > 0 AND the
 * relevant average is non-null (the API may return null averages even
 * when sampleCount > 0 in degenerate edge cases).
 */
function computeEstimatedTotal(
  audioDurationSeconds: number | null,
  timingStats: JobTimingStatsData | null
): number | null {
  if (!audioDurationSeconds) return null;
  const hasAnalyzingHistory = (timingStats?.sampleCount ?? 0) > 0 && timingStats?.avgAnalyzingSecondsPerAudioSecond != null;
  const hasAbbreviatingHistory = (timingStats?.sampleCount ?? 0) > 0 && timingStats?.avgAbbreviatingSeconds != null;
  const analyzingRatio = hasAnalyzingHistory ? timingStats!.avgAnalyzingSecondsPerAudioSecond! : HEURISTIC_ANALYZING_RATIO;
  const analyzingOverhead = hasAnalyzingHistory ? 0 : HEURISTIC_ANALYZING_OVERHEAD;
  const abbreviatingSec = hasAbbreviatingHistory ? timingStats!.avgAbbreviatingSeconds! : HEURISTIC_ABBREVIATING_SECONDS;
  return Math.ceil(audioDurationSeconds * analyzingRatio) + analyzingOverhead + abbreviatingSec;
}

function computeAnalyzingEstimate(
  audioDurationSeconds: number | null,
  timingStats: JobTimingStatsData | null
): number | null {
  if (!audioDurationSeconds) return null;
  const hasAnalyzingHistory = (timingStats?.sampleCount ?? 0) > 0 && timingStats?.avgAnalyzingSecondsPerAudioSecond != null;
  const analyzingRatio = hasAnalyzingHistory ? timingStats!.avgAnalyzingSecondsPerAudioSecond! : HEURISTIC_ANALYZING_RATIO;
  const analyzingOverhead = hasAnalyzingHistory ? 0 : HEURISTIC_ANALYZING_OVERHEAD;
  return Math.ceil(audioDurationSeconds * analyzingRatio) + analyzingOverhead;
}

interface QueuePositionData {
  status: string;
  jobsAhead: number;
  position: number | null;
  holdingSlot: boolean;
  queueLength: number;
  activeSlotCount: number;
  maxSlots: number;
  estimatedWaitSeconds: number | null;
  sampleCount: number;
}

interface ProcessingTimeCardProps {
  status: string;
  elapsedSeconds: number;
  originalDurationSeconds: number | null;
  timingStats: JobTimingStatsData | null;
  transcriptProgress: string | null;
  queuePosition: QueuePositionData | null;
}

function ProcessingTimeCard({ status, elapsedSeconds, originalDurationSeconds, timingStats, transcriptProgress, queuePosition }: ProcessingTimeCardProps) {
  const analyzingEstimate = computeAnalyzingEstimate(originalDurationSeconds, timingStats);
  const totalEstimate = computeEstimatedTotal(originalDurationSeconds, timingStats);

  let remainingSeconds: number | null = null;
  let stageLabel: string;
  let stageHint: string;

  if (status === "queued") {
    stageLabel = "Queued";
    // When the live queue snapshot is in hand we surface jobs-ahead in the
    // hint text so the user always sees ETA + position together. Falls back
    // to the original copy until the first poll lands.
    if (queuePosition && queuePosition.position !== null) {
      const ahead = queuePosition.jobsAhead;
      stageHint = ahead === 0
        ? "You're next — waiting for an open slot"
        : ahead === 1
          ? "1 job ahead of you"
          : `${ahead} jobs ahead of you`;
    } else if (queuePosition && queuePosition.holdingSlot) {
      stageHint = "Slot acquired — starting now…";
    } else {
      stageHint = "Waiting to start…";
    }
    if (queuePosition?.estimatedWaitSeconds != null && queuePosition.estimatedWaitSeconds > 0) {
      remainingSeconds = queuePosition.estimatedWaitSeconds;
    }
  } else if (status === "analyzing") {
    stageLabel = "Transcribing";
    stageHint = "Transcribing speech and prepping audio in parallel";
    if (analyzingEstimate !== null) {
      remainingSeconds = analyzingEstimate - elapsedSeconds;
    }
  } else {
    stageLabel = "Abbreviating";
    stageHint = "AI is selecting segments and stitching audio";
    if (totalEstimate !== null) {
      remainingSeconds = totalEstimate - elapsedSeconds;
    }
  }

  return (
    <div className="bg-secondary/30 rounded-lg p-4 font-mono text-sm space-y-3">
      <div className="flex items-center justify-center gap-2 text-muted-foreground">
        <Activity className="h-4 w-4 text-primary animate-pulse" />
        <span>Engine is currently <span className="text-primary">{status}</span>…</span>
      </div>

      <div className="flex items-center justify-between gap-4 px-2">
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <Timer className="h-3.5 w-3.5" />
          <span className="text-xs uppercase tracking-wider">Elapsed</span>
          <span className="text-foreground font-bold ml-1">{formatElapsed(elapsedSeconds)}</span>
        </div>

        {remainingSeconds !== null && (
          <div className="flex items-center gap-1.5 text-muted-foreground">
            <Clock className="h-3.5 w-3.5" />
            <span className="text-xs uppercase tracking-wider">Est. remaining</span>
            <span className={`font-bold ml-1 ${remainingSeconds <= 0 ? "text-primary" : "text-foreground"}`}>
              {remainingSeconds > 0
                ? formatApprox(remainingSeconds)
                : remainingSeconds >= -90
                  ? "almost done"
                  : originalDurationSeconds && originalDurationSeconds > 0
                    ? `~${Math.ceil(originalDurationSeconds / 60 / 3)} min expected for a ${Math.round(originalDurationSeconds / 60)}-min episode`
                    : "taking longer than expected…"}
            </span>
          </div>
        )}
      </div>

      {status === "analyzing" && transcriptProgress && (
        <TranscriptProgressBar transcriptProgress={transcriptProgress} />
      )}

      {(originalDurationSeconds || status === "queued") && (
        <div
          className="flex items-center justify-center gap-1.5 text-muted-foreground/60 text-xs"
          data-testid={status === "queued" ? "queue-position-hint" : undefined}
        >
          <Info className="h-3 w-3" />
          <span>{stageLabel}: {stageHint}</span>
        </div>
      )}
    </div>
  );
}

const TRANSCRIPT_STEPS: { key: string; label: string }[] = [
  { key: "queued", label: "Queued" },
  { key: "processing", label: "Transcribing" },
  { key: "completed", label: "Done" },
];

function TranscriptProgressBar({ transcriptProgress }: { transcriptProgress: string }) {
  const currentIdx = TRANSCRIPT_STEPS.findIndex((s) => s.key === transcriptProgress);

  return (
    <div className="space-y-1.5 pt-1">
      <div className="flex items-center px-1 text-xs text-muted-foreground/60 uppercase tracking-wider">
        <span>Speech-to-text</span>
      </div>
      <div className="relative flex items-center">
        {TRANSCRIPT_STEPS.map((step, idx) => {
          const isCompleted = idx < currentIdx;
          const isActive = idx === currentIdx;
          const isLast = idx === TRANSCRIPT_STEPS.length - 1;
          return (
            <div key={step.key} className="flex items-center flex-1 last:flex-none">
              <div className="flex flex-col items-center gap-1 min-w-[4rem]">
                <div
                  className={`h-2 w-2 rounded-full transition-colors duration-300 ${
                    isCompleted
                      ? "bg-primary"
                      : isActive
                      ? "bg-primary/70 ring-2 ring-primary/30"
                      : "bg-secondary"
                  }`}
                />
                <span
                  className={`text-[10px] font-mono leading-none ${
                    isActive ? "text-primary" : isCompleted ? "text-primary/70" : "text-muted-foreground/40"
                  }`}
                >
                  {step.label}
                </span>
              </div>
              {!isLast && (
                <div className="flex-1 h-px mb-3 mx-1 bg-secondary overflow-hidden">
                  <div
                    className="h-full bg-primary transition-all duration-500"
                    style={{ width: isCompleted ? "100%" : "0%" }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
